import { requestAnimationFrame } from '@mecontrol/web-inline';
import he from 'he';

// Variables used by delayUntilAnimationFrame() below
let queuedCallbacks: (() => void)[] = [];
let callbackId: number | undefined;

/**
 * Delay running a function until the appropriate time in the browser
 * layout recalculation.
 * Uses the double rAF trick.
 * @param callback Function to delay running
 */
export function delayUntilAnimationFrame(callback: () => void): void {
    // Queue up our function
    queuedCallbacks.push(callback);

    // Only set up the flushing of the queue when we haven't done it yet
    if (callbackId === undefined) {
        callbackId = requestAnimationFrame(() => requestAnimationFrame(() => {
            // Call all queued functions
            for (let i = 0; i < queuedCallbacks.length; i++) {
                queuedCallbacks[i]();
            }

            // Reset variables
            queuedCallbacks = [];
            callbackId = undefined;
        }));
    }
}

/**
 * Get the bounding Client Rect for an element. If the element is undefined,
 * returns a Client Rect with all zeros.
 * @param element An element for which to compute the ClientRect
 * @param sizeBuffer A unit of pixes to expand or contract the rect limits by. Default is zero.
 */
export function getClientRect(element: Element | undefined, sizeBuffer: number = 0): ClientRect {
    if (!element) {
        return { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
    }

    // Need to compute width and height for IE8
    let rect = element.getBoundingClientRect();
    return {
        left: rect.left - sizeBuffer,
        top: rect.top - sizeBuffer,
        right: rect.right + sizeBuffer,
        bottom: rect.bottom + sizeBuffer,
        width: (rect.right - rect.left) + sizeBuffer * 2,
        height: (rect.bottom - rect.top) + sizeBuffer * 2
    };
}

/**
 * Get a ClientRect representing the visible area of the window
 */
export function getVisibleWindowRect(): ClientRect {
    const docEl = document.documentElement!;
    const width = Math.min(window.innerWidth, docEl.clientWidth);
    const height = Math.min(window.innerHeight, docEl.clientHeight);

    return { left: 0, top: 0, right: width, bottom: height, width, height };
}

/**
 * Get a ClientRect which will define the size of the full documentElement
 */
export function getDocumentRect(): ClientRect {
    const docEl = document.documentElement!;
    const width = Math.max(docEl.clientWidth, docEl.offsetWidth, docEl.scrollWidth);
    const height = Math.max(docEl.clientHeight, docEl.offsetHeight, docEl.scrollHeight);

    return { left: 0, top: 0, right: width, bottom: height, width, height };
}

export function decodeHtml(str: string | undefined): string | undefined {
    if (str) {
        return he.decode(str);
    }
}